import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Star, Image as ImageIcon, Check, X } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { toast } from "sonner";
import StoreScopeTag from "@/components/partner/StoreScopeTag";
import { playChime } from "@/features/staffTill/helpers";

/**
 * Iter-442+ §4 — vendor "Rewards" section: review approval queue,
 * proof screenshots, decisions, a compact rewards report and the
 * min-bill setting (0–20, 0 = no minimum, new default).
 * Iter-644 — pill/buttons follow `state` (not vendor_decision), proof
 * gated on `has_screenshot`, posted/approved/rejected timestamps.
 * Endpoints (vendor-owner JWT + X-App-Platform, X-Store-Id aware):
 *   GET  /vendors/me/reviews?status=pending|approved|all (+pending_count)
 *   GET  /vendors/me/reviews/{post_id}/screenshot  (screenshot_b64 = data URI)
 *   POST /vendors/me/reviews/{post_id}/decision {approve, note}
 *   GET  /vendors/me/rewards-report
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

// Iter-644 — en-NZ date + time on every row, e.g. "16 Sep 2026, 10:44 pm".
// Manual month names: Intl "en-NZ" renders September as "Sept", the
// founder spec (and the app dashboard) uses 3-letter months.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtDateTime(x) {
  if (!x) return null;
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return null;
  let h = d.getHours();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${h}:${m} ${ampm}`;
}

export default function VendorReviewsCard() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending");

  const meQ = useQuery({
    queryKey: ["vendor-me"],
    retry: false,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me", OPTS)).data,
  });

  const reviewsQ = useQuery({
    queryKey: ["vendor-reviews", tab],
    enabled: !!meQ.data?.id,
    retry: 0,
    // Iter 684 — new pending reviews must land fast: poll every 8 s while
    // the dashboard is focused (react-query pauses in background tabs).
    refetchInterval: 8_000,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/reviews", {
        ...OPTS,
        params: { ...OPTS.params, status: tab },
      })).data,
  });

  // Iter 684 — play the till chime when a NEW pending review arrives.
  const prevPendingRef = useRef(null);
  useEffect(() => {
    const n = reviewsQ.data?.pending_count;
    if (n == null) return;
    if (prevPendingRef.current != null && n > prevPendingRef.current) playChime("session");
    prevPendingRef.current = n;
  }, [reviewsQ.data?.pending_count]);

  const reportQ = useQuery({
    queryKey: ["vendor-rewards-report"],
    enabled: !!meQ.data?.id,
    retry: 0,
    staleTime: 60_000,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/rewards-report", OPTS)).data,
  });

  const decisionMut = useMutation({
    mutationFn: async ({ postId, approve, note }) =>
      (await sharedApi.post(
        `/classifieds/engagement/vendors/me/reviews/${postId}/decision`,
        { approve, note: note || undefined },
        OPTS,
      )).data,
    onSuccess: (_, { approve }) => {
      toast.success(approve ? "Review approved — points credited." : "Review rejected — the customer has been emailed.");
      qc.invalidateQueries({ queryKey: ["vendor-reviews"] });
      qc.invalidateQueries({ queryKey: ["vendor-rewards-report"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save the decision — please try again.");
    },
  });

  // Iter-462 follow-up fix — the screenshot endpoint returns JSON
  // {screenshot_b64} (all states, verified by backend), NOT binary.
  // The old blob + window.open approach rendered nothing and got
  // popup-blocked. Render the b64 in an in-page lightbox instead.
  // Iter-644 — screenshot_b64 is now ALWAYS a full data URI; a 404
  // carries a human "claim only — verify on {platform}" detail message.
  const [proof, setProof] = useState(null); // { postId, src, postUrl } | { postId, loading }
  const openScreenshot = async (postId, postUrl) => {
    setProof({ postId, loading: true });
    try {
      const { data } = await sharedApi.get(
        `/classifieds/engagement/vendors/me/reviews/${postId}/screenshot`,
        OPTS,
      );
      const b64 = data?.screenshot_b64;
      if (!b64) throw new Error("empty");
      // Iter-644 — preview/new backend sends a full data URI. Old prod
      // responses are RAW base64 with no content_type — sniff the magic
      // bytes so JPEG screenshots don't break behind an image/png label.
      const sniffed = b64.startsWith("/9j/") ? "image/jpeg"
        : b64.startsWith("iVBOR") ? "image/png"
        : b64.startsWith("UklGR") ? "image/webp"
        : null;
      const src = b64.startsWith("data:") || b64.startsWith("http")
        ? b64
        : `data:${data?.content_type || sniffed || "image/png"};base64,${b64}`;
      setProof({ postId, src, postUrl: postUrl || data?.post_url || null });
    } catch (err) {
      setProof(null);
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d.trim() ? d : "No screenshot available for this review.");
    }
  };

  if (meQ.isLoading || meQ.isError) return null;

  const reviews = reviewsQ.data?.reviews || reviewsQ.data?.items || [];
  const pendingCount = reviewsQ.data?.pending_count ?? 0;
  const report = reportQ.data || null;

  return (
    <section id="reviews" data-testid="vendor-reviews-card" className="mt-6 rounded-2xl bg-white shadow-card p-6 md:p-8">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-2xl font-black text-slate-900">Rewards &amp; reviews</h2>
        <StoreScopeTag />
        {pendingCount > 0 && (
          <span data-testid="vendor-reviews-pending-badge" className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 text-[11px] font-black">
            {pendingCount} awaiting approval
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Approve customer review proofs to credit their points instantly — rejections send them a kind email.
      </p>

      {report && (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600" data-testid="vendor-rewards-report">
          {report.points_credited != null && <span>Points credited <b className="text-slate-900">{report.points_credited}</b></span>}
          {report.reviews_approved != null && <span>Approved <b className="text-slate-900">{report.reviews_approved}</b></span>}
          {report.reviews_pending != null && <span>Pending <b className="text-slate-900">{report.reviews_pending}</b></span>}
          {report.deferred_payables_nzd != null && <span>Deferred payables <b className="text-slate-900">${Number(report.deferred_payables_nzd).toFixed(2)}</b></span>}
        </div>
      )}

      <div className="mt-4 flex gap-2">
        {["pending", "approved", "all"].map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`vendor-reviews-tab-${t}`}
            onClick={() => setTab(t)}
            className={`rounded-full text-xs font-bold px-4 py-1.5 capitalize transition-colors ${
              tab === t ? "bg-slate-900 text-white" : "bg-white text-slate-500 border border-slate-200 hover:border-slate-400"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {reviewsQ.isLoading ? (
        <div className="mt-6 grid place-items-center py-6 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : reviews.length === 0 ? (
        <p className="mt-5 text-sm text-slate-500" data-testid="vendor-reviews-empty">
          Nothing here yet — reviews appear as customers post them.
        </p>
      ) : (
        <ul className="mt-4 space-y-3" data-testid="vendor-reviews-list">
          {reviews.map((r) => (
            <ReviewRow
              key={r.id || r.post_id}
              r={r}
              busy={decisionMut.isPending}
              proofLoading={proof?.loading && proof?.postId === (r.id || r.post_id)}
              onDecision={(approve, note) => decisionMut.mutate({ postId: r.id || r.post_id, approve, note })}
              onScreenshot={() => openScreenshot(r.id || r.post_id, r.post_url)}
            />
          ))}
        </ul>
      )}

      {/* Iter 664 — 48 h auto-approve + 12 h SMS reminder footer note. */}
      <p className="mt-4 text-xs text-slate-500" data-testid="vendor-reviews-autoapprove-note">
        Unanswered reviews are approved automatically after 48 hours and the customer&apos;s
        points are credited. You&apos;ll get a text reminder at 12 hours.
      </p>

      {/* Proof lightbox */}
      {proof && !proof.loading && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm grid place-items-center p-4"
          data-testid="vendor-proof-lightbox"
          onClick={() => setProof(null)}
        >
          <div className="relative max-w-3xl w-fit" onClick={(e) => e.stopPropagation()}>
            <img
              src={proof.src}
              alt="Review proof screenshot"
              data-testid="vendor-proof-image"
              className="max-h-[80vh] w-auto rounded-xl shadow-2xl bg-white"
            />
            {proof.postUrl && (
              <a
                href={proof.postUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="vendor-proof-open-post"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white text-slate-800 text-xs font-bold px-4 py-2 shadow-card hover:bg-slate-100 transition-colors"
              >
                Open post ↗
              </a>
            )}
            <button
              type="button"
              onClick={() => setProof(null)}
              aria-label="Close proof"
              data-testid="vendor-proof-close"
              className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-white text-slate-800 shadow-card grid place-items-center hover:bg-slate-100 transition-colors"
            >
              <X className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function ReviewRow({ r, busy, proofLoading, onDecision, onScreenshot }) {
  const [note, setNote] = useState("");
  const postId = r.id || r.post_id;
  // Iter-644 — `state` drives the pill AND the buttons (the old code read
  // `r.status`, which the API never sends → every row showed "pending"
  // with Approve/Reject even on the Approved tab).
  const state = r.state || "pending";
  const pending = state === "pending";
  const customerName = r.customer?.name || r.customer_name || "Customer";
  const posted = fmtDateTime(r.posted_at || r.submitted_at);
  const approvedAt = fmtDateTime(r.approved_at || r.decided_at);
  const rejectedAt = fmtDateTime(r.decided_at);
  const deadline = fmtDateTime(r.review_deadline);
  // Iter 664 — rows decided by the 48 h sweep show "Auto-approved (48 h)"
  // instead of an owner name.
  const auto48h = (r.vendor_decision?.by || r.decided_by) === "system_auto_48h";
  return (
    <li className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`vd-review-${postId}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <Star className="w-4 h-4 text-amber-500 shrink-0" />
        <span className="font-bold text-slate-900">{customerName}</span>
        {r.platform && <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{r.platform}</span>}
        <StatePill state={state} postId={postId} />
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {[r.customer?.email || r.customer_email, r.customer?.phone || r.customer_phone].filter(Boolean).join(" · ")}
      </div>
      {/* Iter-644 — posted / approved / rejected timestamps on every row. */}
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
        {posted && <span data-testid={`vd-posted-${postId}`}>📝 Posted {posted}</span>}
        {/* Iter 685 — notification receipts (684 backend): hide entirely on
            older posts where notify_channels is null. */}
        {r.notify_channels && (
          <span className="basis-full" data-testid={`vd-receipts-${postId}`}>
            🔔 SMS {r.notify_channels.sms ? "sent ✅" : "not sent ❌"} · Email{" "}
            {r.notify_channels.email ? "sent ✅" : "not sent ❌"}
            {r.notified_at ? ` · ${fmtDateTime(r.notified_at)}` : ""}
          </span>
        )}
        {state === "approved" && approvedAt && (
          <span className="text-emerald-700 font-semibold" data-testid={`vd-approved-${postId}`}>
            {auto48h
              ? `✓ Auto-approved (48 h) ${approvedAt}`
              : `✓ Approved ${approvedAt}${r.decision === "auto_verified" ? " · auto-verified" : ""}`}
          </span>
        )}
        {/* Iter-646 — which vendor tag the checker matched (url scan or
            screenshot check). Empty = manual vendor decision. */}
        {state === "approved" && r.matched_tags?.length > 0 && (
          <span className="basis-full text-emerald-700" data-testid={`vd-matched-${postId}`}>
            ✓ Auto-verified · matched {r.matched_tags.map((t) => (String(t).startsWith("@") ? t : `@${t}`)).join(" ")}
            {r.verification_method === "url_tag_match" ? " (link scan)" : r.verification_method === "ai_screenshot" ? " (screenshot check)" : ""}
          </span>
        )}
        {state === "rejected" && rejectedAt && (
          <span className="text-red-600 font-semibold" data-testid={`vd-rejected-${postId}`}>
            ✗ Rejected {rejectedAt}
          </span>
        )}
        {pending && deadline && (
          <span className="text-amber-700 font-semibold" data-testid={`vd-deadline-${postId}`}>
            ⏳ Post by {deadline}
          </span>
        )}
      </div>
      {/* Iter 676 §B — one-glance post preview (backend-fetched oEmbed/OG).
          Render only when there's an image or title; otherwise the proof
          button / "no screenshot" note below stays the fallback. */}
      {(r.post_preview?.image || r.post_preview?.title) && (
        <a
          href={r.post_url || r.post_preview?.url || undefined}
          target="_blank"
          rel="noreferrer"
          data-testid={`vd-preview-${postId}`}
          className="mt-2.5 flex items-center gap-3 rounded-xl bg-white border border-slate-200 hover:border-slate-400 p-2 transition-colors"
        >
          {r.post_preview?.image && (
            <img
              src={r.post_preview.image}
              alt=""
              loading="lazy"
              className="w-16 h-16 rounded-lg object-cover shrink-0 bg-slate-100"
            />
          )}
          <span className="min-w-0 flex-1">
            {r.post_preview?.title && (
              <span className="block text-xs font-bold text-slate-900 leading-snug line-clamp-2">
                {r.post_preview.title}
              </span>
            )}
            <span className="mt-0.5 block text-[11px] text-slate-500 truncate">
              {[r.post_preview?.author, r.post_preview?.site, "tap to open"].filter(Boolean).join(" · ")}
            </span>
          </span>
        </a>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {/* Iter-644 — View proof ONLY when a screenshot actually exists;
            claim-only posts get the "verify on {platform}" note instead. */}
        {r.has_screenshot ? (
          <button type="button" onClick={onScreenshot} disabled={proofLoading} data-testid={`vd-proof-${postId}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-bold px-3.5 py-1.5 transition-colors disabled:opacity-60">
            {proofLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} View proof
          </button>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold px-3.5 py-1.5"
            data-testid={`vd-noproof-${postId}`}
          >
            🔗 No screenshot — customer claimed the post · verify on {r.platform || "the platform"}
          </span>
        )}
        {r.post_url && (
          <a href={r.post_url} target="_blank" rel="noreferrer" data-testid={`vd-open-post-${postId}`}
            className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-bold px-3.5 py-1.5 transition-colors">
            Open post ↗
          </a>
        )}
        {pending && (
          <>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)"
              data-testid="vendor-review-note"
              className="flex-1 min-w-[140px] rounded-full bg-white border border-slate-200 px-3.5 py-1.5 text-xs focus:outline-none focus:border-slate-400"
            />
            <button type="button" disabled={busy} onClick={() => onDecision(true, note)} data-testid="vendor-review-approve"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-1.5 transition-colors disabled:opacity-60">
              <Check className="w-3.5 h-3.5" /> Approve
            </button>
            <button type="button" disabled={busy} onClick={() => onDecision(false, note)} data-testid="vendor-review-reject"
              className="inline-flex items-center gap-1 rounded-full bg-white border border-red-200 hover:bg-red-50 text-red-600 text-xs font-bold px-4 py-1.5 transition-colors disabled:opacity-60">
              <X className="w-3.5 h-3.5" /> Reject
            </button>
          </>
        )}
      </div>
    </li>
  );
}

// Iter-644 — pill text/colour from `state`.
function StatePill({ state, postId }) {
  const cls =
    state === "approved"
      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
      : state === "rejected"
      ? "bg-red-50 border-red-200 text-red-600"
      : "bg-amber-50 border-amber-200 text-amber-700";
  const label = state === "approved" ? "✓ Approved" : state === "rejected" ? "✗ Rejected" : "⏳ Pending";
  return (
    <span
      data-testid={`vd-state-${postId}`}
      className={`ml-auto inline-flex items-center rounded-full border text-[11px] font-black px-2.5 py-0.5 ${cls}`}
    >
      {label}
    </span>
  );
}
